import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { formatPrice, isPriceOnRequest } from '@alkor/utils';
import { openManagerChat } from '@/lib/telegram';
import { IconBell, IconCheck, IconHeart, IconMessage } from '@/components/icons';
import {
  Accordion, Badge, Notice, Rail, RailSkeleton, StatusChip, ErrorState, TileSkeleton,
} from '@/components/ui';
import { isNetworkError, isPaused } from '@/lib/online';
import { Gallery } from '@/components/Gallery';
import { MainButton } from '@/components/MainButton';
import { specsFromDescription } from '@/lib/specs';

const MANAGER = (import.meta.env.VITE_MANAGER_USERNAME as string | undefined) ?? 'alkor_manager';

export function ProductScreen() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { inCart, addToCart, isFavorite, toggleFavorite, isSubscribed, subscribeStock, busy } = useShop();

  const productQuery = useQuery({
    queryKey: ['product', slug],
    queryFn: () => api.product(slug),
    staleTime: 60_000,
  });

  const relatedQuery = useQuery({
    queryKey: ['related', slug],
    queryFn: () => api.related(slug),
    staleTime: 300_000,
    enabled: Boolean(productQuery.data),
  });

  const product = productQuery.data;

  useEffect(() => {
    if (product) track('product_view', { productId: product.id, props: { slug: product.slug } });
  }, [product]);

  // Без связи запрос стоит на паузе: ошибки не будет, поэтому «нет связи»
  // показываем раньше скелетона
  if (isPaused(productQuery)) {
    return <ErrorState offline onRetry={() => void productQuery.refetch()} />;
  }

  if (productQuery.isLoading) {
    return (
      <>
        <div className="gallery" aria-hidden="true"><div className="skel" style={{ width: '78%', height: '78%' }} /></div>
        <div className="block"><TileSkeleton count={2} /></div>
      </>
    );
  }

  if (productQuery.isError || !product) {
    return (
      <ErrorState
        message={(productQuery.error as Error)?.message ?? 'Товар не найден'}
        offline={isNetworkError(productQuery.error)}
        onRetry={() => void productQuery.refetch()}
      />
    );
  }

  const out = product.availability === 'out_of_stock';
  const onRequest = isPriceOnRequest(product.price);
  const fav = isFavorite(product.id);
  const subscribed = isSubscribed(product.id);
  const images = product.images;

  // Товар без цены в корзину не кладётся: главная кнопка ведёт к менеджеру
  const requestPrice = () => {
    track('manager_contact', { productId: product.id, props: { source: 'price_request' } });
    void api.createLead({
      message: `Запрос цены: ${product.name} (${product.sku})`,
      source: 'miniapp_product',
      contextProductId: product.id,
    }).catch(() => undefined);
    openManagerChat(MANAGER, `Здравствуйте! Подскажите цену на ${product.name} (${product.sku})`);
  };

  const mainButton = out
    ? subscribed
      ? <MainButton text="Вы подписаны" disabled hint="Напишем в день поступления" />
      : <MainButton text="Сообщить о поступлении" onClick={() => void subscribeStock(product)} loading={busy} />
    : onRequest
      ? <MainButton text="Запросить цену" hint="Менеджер ответит в Telegram" onClick={requestPrice} />
      : inCart(product.id)
        ? <MainButton text="В корзине · перейти" onClick={() => navigate('/cart')} />
        : <MainButton text="В корзину" suffix={formatPrice(product.price)} loading={busy} onClick={() => void addToCart(product)} />;

  return (
    <>
      <div className="gallery x-corners">
        <span className="x-corners__b" aria-hidden="true" />
        <Gallery images={images} alt={product.name} artKind={product.categorySlug} />

        <div className="gallery__badge"><Badge product={product} /></div>

        <button
          type="button"
          className={`headbtn${fav ? ' headbtn--on' : ''} gallery__fav`}
          style={{ background: 'var(--c-bg)', borderColor: 'var(--c-border)', color: fav ? 'var(--c-accent)' : 'var(--c-text-2)' }}
          aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
          onClick={() => void toggleFavorite(product)}
        >
          <IconHeart filled={fav} />
        </button>
      </div>

      <div className="pdetail">
        {product.brand && <div className="pdetail__brand">{product.brand}</div>}
        <h2>{product.name}</h2>

        <div className="x-price">
          <small>Цена</small>
          <span className="x-price__value">
            {onRequest ? (
              <span className="price price--lg price--request">Цена по запросу</span>
            ) : (
              <>
                {product.oldPrice && <span className="price price--old">{formatPrice(product.oldPrice)}</span>}
                <span className="price price--lg">{formatPrice(product.price)}</span>
              </>
            )}
          </span>
        </div>
        <div className="x-price__meta">
          <StatusChip status={product.availability} leadTimeDays={product.leadTimeDays} />
          <span className="pdetail__sku" style={{ marginTop: 0 }}>Арт. {product.sku}</span>
        </div>

        {onRequest && !out && (
          <div style={{ margin: '16px -16px 0' }}>
            <Notice variant="info">
              Цену и срок поставки уточнит менеджер. Нажмите «Запросить цену» — ответим в Telegram.
            </Notice>
          </div>
        )}

        {out && (
          <div style={{ margin: '16px -16px 0' }}>
            <Notice variant="warn">
              Товара нет на складе. Подпишитесь — бот напишет в день поступления.
            </Notice>
          </div>
        )}

        {product.shortDescription && <p className="lede">{product.shortDescription}</p>}

        {product.highlights.length > 0 && (
          <ul className="hl">
            {product.highlights.map((h, i) => (
              <li key={i}>
                <IconCheck size={16} />
                {h}
              </li>
            ))}
          </ul>
        )}

        <div className="acc">
          {product.specifications.length > 0 && (
            <Accordion title="Характеристики" defaultOpen>
              {product.specifications.map((g, i) => (
                <div className="specgroup" key={i}>
                  <h5>{g.group}</h5>
                  {g.items.map((it, j) => (
                    <div className="specrow" key={j}>
                      <span>{it.label}</span>
                      <b>{it.value}</b>
                    </div>
                  ))}
                </div>
              ))}
            </Accordion>
          )}

          {product.specifications.length === 0 && specsFromDescription(product.description).length > 0 && (
            <Accordion title="Характеристики" defaultOpen>
              <div className="specgroup">
                {specsFromDescription(product.description).map((it, j) => (
                  <div className="specrow" key={j}>
                    <span>{it.label}</span>
                    <b>{it.value}</b>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          {product.description && (
            <Accordion title="Описание"><p>{product.description}</p></Accordion>
          )}

          {product.packageContents && (
            <Accordion title="Комплектация"><p>{product.packageContents}</p></Accordion>
          )}

          <Accordion title="Доставка и оплата">
            <p>
              Самовывоз — шоурум Москва, ул. Кусковская, 20А, по договорённости.<br />
              Курьер по городу — в день заказа при оформлении до 15:00.<br />
              Транспортная компания — по всей России, 2–7 дней.<br />
              <br />
              Наличный расчёт, карта, безнал для ИП и юрлиц.
            </p>
          </Accordion>
        </div>
      </div>

      {/* Рекомендации грузятся отдельным запросом: пока он идёт, вместо
          пустоты стоит скелетон — иначе низ карточки «дорисовывается» рывком */}
      {relatedQuery.isLoading ? (
        <RailSkeleton title="С этим товаром берут" />
      ) : (
        <>
          <Rail title="С этим товаром берут" items={relatedQuery.data?.accessories ?? []} />
          <Rail title="Похожие товары" items={relatedQuery.data?.similar ?? []} />
        </>
      )}

      <div className="block" style={{ marginTop: 24 }}>
        <button
          type="button"
          className="secbtn"
          onClick={() => {
            track('manager_contact', { productId: product.id, props: { source: 'product' } });
            void api.createLead({
              message: `Вопрос по товару: ${product.name} (${product.sku})`,
              source: 'miniapp_product',
              contextProductId: product.id,
            }).catch(() => undefined);
            openManagerChat(MANAGER, `Здравствуйте! Вопрос по товару ${product.name} (${product.sku})`);
          }}
        >
          <IconMessage size={17} />
          Написать менеджеру
        </button>
      </div>

      {out && !subscribed && (
        <div className="block" style={{ marginTop: 12 }}>
          <button type="button" className="secbtn" onClick={() => void subscribeStock(product)}>
            <IconBell size={17} />
            Сообщить о поступлении
          </button>
        </div>
      )}

      <div className="pad" />
      {mainButton}
    </>
  );
}
